// src/layouts/DashboardLayout.js
import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Button, Dropdown, Tooltip } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  DashboardOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  SettingOutlined,
  EditOutlined,
  BarChartOutlined,
  BellOutlined,
  LogoutOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { supabase } from '../services/supabase';
import './DashboardLayout.css';

const { Header, Sider, Content, Footer } = Layout;

const DashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [company, setCompany] = useState(null);
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchCompanyData();
  }, []);

 const fetchCompanyData = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No user found');

    // First, try to get user profile with name
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', user.id)
      .single();

    if (!profileError && profileData) {
      // If profile exists and has name data
      const firstName = profileData.first_name || '';
      const lastName = profileData.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();

      if (fullName) {
        setUserName(fullName);
      } else {
        // Fallback to email if no name in profile
        setUserName(user.email?.split('@')[0] || 'User');
      }
    } else {
      // Fallback to email if no profile found
      setUserName(user.email?.split('@')[0] || 'User');
    }

    // Then fetch company data as before
    const { data: companyData, error } = await supabase
      .from('companies')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    setCompany(companyData);
  } catch (error) {
    console.error('Error fetching company:', error);
  }
};

  const currentKey = location.pathname.split('/')[2] || 'dashboard';

  const getCurrentPageTitle = () => {
    const menuItem = menuItems.find(item => item.key === currentKey);
    return menuItem ? menuItem.label : 'Dashboard';
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: 'employees',
      icon: <TeamOutlined />,
      label: 'Employees',
      onClick: () => navigate('/dashboard/employees'),
    },
    {
      key: 'attendance',
      icon: <ClockCircleOutlined />,
      label: 'Attendance',
      onClick: () => navigate('/dashboard/attendance'),
    },
    {
      key: 'payroll',
      icon: <DollarOutlined />,
      label: 'Payroll',
      onClick: () => navigate('/dashboard/payroll'),
    },
    {
      key: 'leaves',
      icon: <EditOutlined/>,
      label: 'Leaves',
      onClick: () => navigate('/dashboard/leaves'),
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: 'Reports',
      onClick: () => navigate('/dashboard/reports'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/dashboard/settings'),
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      label: 'My Profile',
      icon: <UserOutlined />,
    },
    {
      key: 'settings',
      label: 'Account Settings',
      icon: <SettingOutlined />,
      onClick: () => navigate('/dashboard/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'signout',
      label: 'Sign Out',
      icon: <LogoutOutlined />,
      onClick: handleSignOut,
      danger: true,
    },
  ];

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/login');
  }

  const companyName = company?.name || 'mySmart';

  return (
    <Layout className="app-shell" hasSider>
      {/* FLOATING SIDEBAR */}
      <Sider
        className="app-sider"
        trigger={null}
        collapsible
        collapsed={collapsed}
        onBreakpoint={(broken) => setCollapsed(broken)}
        breakpoint="lg"
        width={232}
        collapsedWidth={76}
      >
        <div className="app-sider__brand">
          {company?.logo_url ? (
            <img className="app-sider__logo" src={company.logo_url} alt={`${companyName} logo`} />
          ) : (
            <div className="app-sider__name">{collapsed ? companyName[0] : companyName}</div>
          )}
        </div>

        <nav className="app-sider__nav" aria-label="Main navigation">
          <Menu mode="inline" selectedKeys={[currentKey]} items={menuItems} />
        </nav>

        <div className="app-sider__powered">
          <img src="/mySmart_logoS.png" alt="mySmart logo" />
          {!collapsed && (
            <span>
              powered by <strong>mySmart</strong>
            </span>
          )}
        </div>
      </Sider>

      <Layout className="app-main">
        {/* STICKY TOP BAR */}
        <Header className="app-topbar">
          <div className="app-topbar__left">
            <Button
              className="app-icon-btn"
              type="text"
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
            <div className="app-crumb">
              <span className="app-crumb__parent">{companyName}</span>
              <span className="app-crumb__current">{getCurrentPageTitle()}</span>
            </div>
          </div>

          <div className="app-topbar__right">
            <Tooltip title="Notifications" placement="bottom">
              <Button className="app-icon-btn" type="text" aria-label="Notifications" icon={<BellOutlined />} />
            </Tooltip>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <div className="app-user" role="button" tabIndex={0} aria-label="Account menu">
                <Avatar className="app-user__avatar" size={34} icon={<UserOutlined />} />
                <span className="app-user__text">
                  <span className="app-user__name">{userName || 'User'}</span>
                  <span className="app-user__role">Admin</span>
                </span>
                <DownOutlined className="app-user__chevron" />
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* CONTENT PANEL */}
        <Content className="app-content">
          <Outlet />
        </Content>

        <Footer className="app-footer">
          © {new Date().getFullYear()} myPayroll by mySmart · All rights reserved
        </Footer>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
